import React, { useContext, useEffect, useState } from "react";
import translate from "../i18n/Translator";
import DialogPopup from "../components/DialogPopup";
import { Button, Grid, Typography } from "@material-ui/core";
import ValidatedInput, { InputRef } from "../components/ValidatedInput";
import { SchedulePaymentRequest, PaymentCfdiRequest, Payment } from "../model/Payment";
import { createPayment, updatePayment, metadata } from "../api/CustomerPaymentAPI";
import { AppContext } from "../context/AppContext";
import { ErrorSnackbar } from "../components/Snackbars";
import { Cfdi } from "../model/Cfdi";
import { PaymentMetadataResponse } from "../model/Payment";
import { CURRENCIES } from "../model/Currency";
import Progress from "../components/Progress";
import Ellipsis from "../components/Ellipsis";
import DateFormat from "../components/DateFormat";
import Divider from "@material-ui/core/Divider/Divider";
import { GTZeroValidator, LTEValidator } from "../components/Validators";
import Error from "../components/Error";
import moment from "moment";
import CurrencyFormat from "../components/CurrencyFormat";
import DatePicker from "../components/DatePicker";

interface NewPaymentPopupProps {
    cfdis: Cfdi[];
    paymentId?: string;
    currency?: string;
    onClose(): any;
    onSuccess(payment: Payment): any;
}

export default function NewPaymentPopup(props: NewPaymentPopupProps) {
    const context = useContext(AppContext);
    const mxnCurrency = "MXN";
    const gtZeroValidator = new GTZeroValidator();

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<PaymentMetadataResponse>();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();
    const [currencies, setCurrencies] = useState<string[]>([]);
    const [currencyLabels, setCurrencyLabels] = useState<string[]>([]);
    const [request, setRequest] = useState<SchedulePaymentRequest>({
        payment_cfdis: [] as PaymentCfdiRequest[],
        total: 0,
    } as SchedulePaymentRequest);
    const [validations, setValidations] = useState<any>({} as any);
    const [disableDate, setDisableDate] = useState(false);

    const submitPromise = () => {
        if (props.paymentId) {
            return updatePayment(context.session!.tenant!.id, context.session!.customer!.id, props.paymentId, request)
        }
        return createPayment(context.session!.tenant!.id, context.session!.customer!.id, request);
    };

    useEffect(() => {
        let total = 0;
        const paymentCfdis = request.payment_cfdis.map((cfdi) => cfdi);
        if (request.currency) {
            paymentCfdis.forEach(paymentCfdi => {
                if (paymentCfdi.currency === request.currency) {
                    paymentCfdi.exchange_rate = 1;
                } else {
                    switch (paymentCfdi.currency) {
                        case "USD":
                            paymentCfdi.exchange_rate = data?.dollar;
                            break;
                        case "EUR":
                            paymentCfdi.exchange_rate = data?.euro;
                            break;
                        default:
                            paymentCfdi.exchange_rate = 1;
                    }
                }
                paymentCfdi.payment = +((paymentCfdi.amount * (paymentCfdi.exchange_rate || 1)).toFixed(2));
            });
            total = +(request.payment_cfdis.map(cfdi => cfdi.payment).reduce((a, b) => a + b).toFixed(2));

            const disableDate = request.payment_cfdis.map(paymentCfdi => paymentCfdi.currency).filter(currency => currency !== request.currency).length > 0;
            setDisableDate(disableDate);
        } else {
            paymentCfdis.forEach(cfdi => {
                cfdi.exchange_rate = undefined;
            });
        }
        setRequest({ ...request, payment_cfdis: paymentCfdis, total: total, date: new Date() });
        // eslint-disable-next-line
    }, [request.currency]);

    useEffect(() => {
        setStatus("loading");
        metadata(context.session!.tenant!.id, context.session!.customer!.id).then(response => {
            setData(response);

            const paymentCfdis = props.cfdis.sort((a, b) => {
                return new Date(a.fecha).getTime() - new Date(b.fecha).getTime();
            }).map((cfdi, index) => {
                return {
                    cfdi_id: cfdi.id,
                    payment: 0,
                    amount: cfdi.metadata?.payment_amount || cfdi.metadata?.balance || 0,
                    balance: cfdi.metadata?.balance || 0,
                    partial: props.cfdis.length === (index + 1),
                    currency: cfdi.moneda || mxnCurrency,
                    serie_folio: [cfdi.serie, cfdi.folio].filter(el => !!el).map(el => el as string).join("-"),
                    uuid: cfdi.complementos?.timbre_fiscal_digital?.uuid,
                    date: cfdi.fecha
                } as PaymentCfdiRequest;
            });

            const allCurrencies = paymentCfdis.map(cfdi => cfdi.currency);
            let requiredCurrencies = CURRENCIES.filter(el => allCurrencies.indexOf(el) >= 0);
            if (requiredCurrencies.indexOf(mxnCurrency) >= 0 || requiredCurrencies.length > 1) {
                // si hay un comprobante en pesos, el depósito siempre debe estar en pesos
                requiredCurrencies = [mxnCurrency];
            } else {
                requiredCurrencies.unshift(mxnCurrency);
            }

            const customerCurrencies = context.session!.customer?.currencies;
            if (customerCurrencies && customerCurrencies.length > 0) {
                requiredCurrencies = requiredCurrencies.filter(currency => customerCurrencies.indexOf(currency) >= 0);
            }

            setCurrencies(requiredCurrencies);
            setCurrencyLabels(requiredCurrencies.map((el) => translate(`currencies.${el}`) as string));

            const allValidations = {
                "currency": {
                    valid: false
                } as InputRef
            } as any;

            paymentCfdis.forEach((cfdi) => {
                allValidations[`amount-${cfdi.cfdi_id}`] = {
                    valid: false
                } as InputRef;
            });

            let defaultCurrency;
            if (props.currency) {
                defaultCurrency = props.currency;
            } else if (requiredCurrencies.length > 0) {
                defaultCurrency = requiredCurrencies[0];
            } else {
                setRequest({ ...request, payment_cfdis: paymentCfdis });
                setStatus(translate("payments.new.no_currency") as string);
                return;
            }

            setRequest({ ...request, payment_cfdis: paymentCfdis, currency: defaultCurrency });
            setStatus("loaded");
        }).catch(error => {
            setStatus(error.message);
        });
        // eslint-disable-next-line
    }, [props.cfdis]);

    const hasChanged = (name: string, value: string, inputRef: InputRef) => {
    };

    const onChangedDate = (name: string, raw?: string, date?: Date) => {
        if (date) {
            setRequest({ ...request, date: date })
        } else {
            delete request.date;
            setRequest(request);
        }
    };

    const hasChangedCurrency = (name: string, value: string, inputRef: InputRef) => {
        setRequest({ ...request, currency: value });
        validations[name] = inputRef;
        setValidations(validations);
    };

    const hasChangedBalance = (cfdi: PaymentCfdiRequest) => (name: string, value: string, inputRef: InputRef) => {
        if (cfdi.exchange_rate) {
            cfdi.amount = +value;
            cfdi.payment = +((cfdi.amount * (cfdi.exchange_rate || 1)).toFixed(2));
        }

        const index = request.payment_cfdis.findIndex(el => el.cfdi_id === cfdi.cfdi_id);
        if (index >= 0) {
            request.payment_cfdis[index] = cfdi;
        }
        setRequest({ ...request, payment_cfdis: request.payment_cfdis });

        const total = +(request.payment_cfdis.map(cfdi => cfdi.payment).reduce((a, b) => a + b).toFixed(2));
        setRequest({ ...request, total: total });

        validations[name] = inputRef;
        setValidations(validations);
    };

    const isValid = () => {
        var valid = true;
        for (let field in validations) {
            let ref = validations[field];
            if (!ref.valid) {
                ref.blurer(true);
                valid = false;
            }
        }
        return valid;
    }

    const onConfirm = () => {
        if (!isValid()) {
            return;
        }

        setSubmitting(true);
        submitPromise().then(payment => {
            props.onSuccess(payment);
        }).catch(error => {
            setSubmitting(false);
            setError(error.message);
        });
    };

    return (
        <DialogPopup open title={translate(props.paymentId ? "payments.edit.title" : "payments.new.title")} maxWidth="md"
            disableEscapeKeyDown={submitting}
            disableBackdropClick={submitting}
            disable={submitting}
            onClose={props.onClose}
            closeColor="default"
            closeText={translate("buttons.cancel") as string}
            button={(
                <Button onClick={onConfirm} variant="outlined" color="primary" disabled={submitting}>
                    {translate(props.paymentId ? "buttons.update_payment" : "buttons.new_payment")}
                </Button>
            )}>
            {status === "loading" && (
                <Progress />
            )}
            {status !== "loading" && status !== "loaded" && (
                <Error text={status} />
            )}
            {status === "loaded" && (
                <Grid container justify="space-between" alignItems="center" spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2">{translate("payments.new.details")}</Typography>
                        <Divider />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ValidatedInput type="text" id="currency" name="currency"
                            value={request.currency} label={translate("currencies.title") as string}
                            options={currencies} optionLabels={currencyLabels}
                            required disabled={currencies.length === 1 || submitting}
                            margin="dense"
                            onValueChanged={hasChangedCurrency} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <DatePicker label={translate("payments.transaction.date") as string}
                            name="date" initial={request.date ? moment(request.date).format("YYYY-MM-DD") : undefined} onChange={onChangedDate}
                            defaultIfMissing clearable={false} autoOK disablePast disableFuture={false} disabled={disableDate} />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4}>
                        <Typography variant="h4" align="right">
                            <CurrencyFormat amount={request.total} currency={request.currency || mxnCurrency} />
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">{translate("payments.new.invoices")}</Typography>
                                <Divider />
                            </Grid>
                            {request.payment_cfdis.map(cfdi => (
                                <Grid item xs={12} key={cfdi.cfdi_id}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} md={4}>
                                            {cfdi.serie_folio && (
                                                <>
                                                    <Ellipsis text={cfdi.serie_folio} lenght={0} uppercased />
                                                    <br />
                                                </>
                                            )}
                                            {cfdi.uuid && (
                                                <>
                                                    <Ellipsis text={cfdi.uuid} lenght={0} uppercased secondary />
                                                    <br />
                                                </>
                                            )}
                                            <DateFormat date={cfdi.date} format="YYYY-MM-DDTHH:mm:ss" />
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2}>
                                            <ValidatedInput type="text" id={`balance-${cfdi.cfdi_id}`} name={`balance-${cfdi.cfdi_id}`}
                                                value={cfdi.balance.toString()}
                                                label={`${translate("cfdis.balance") as string} ${cfdi.currency}`}
                                                required disabled
                                                margin="dense"
                                                onValueChanged={hasChanged} />
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2}>
                                            <ValidatedInput type="text" id={`amount-${cfdi.cfdi_id}`} name={`amount-${cfdi.cfdi_id}`}
                                                value={cfdi.amount.toString()} label={cfdi.currency}
                                                validators={[gtZeroValidator, new LTEValidator(cfdi.balance)]}
                                                required disabled={!cfdi.partial || submitting}
                                                margin="dense"
                                                onValueChanged={hasChangedBalance(cfdi)} />
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2}>
                                            <ValidatedInput type="text" id={`exchangerate-${cfdi.cfdi_id}`} name={`exchangerate-${cfdi.cfdi_id}`}
                                                value={cfdi.exchange_rate ? cfdi.exchange_rate.toString() : ""}
                                                label={translate("payments.new.exchange_rate") as string}
                                                required disabled
                                                margin="dense"
                                                onValueChanged={hasChanged} />
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2}>
                                            <ValidatedInput type="text" id={`payment-${cfdi.cfdi_id}`} name={`payment-${cfdi.cfdi_id}`}
                                                value={cfdi.payment.toString()} label={request.currency || mxnCurrency}
                                                required disabled
                                                margin="dense"
                                                onValueChanged={hasChanged} />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            )}
            <ErrorSnackbar message={error} onClose={() => setError(undefined)} />
        </DialogPopup>
    );

}